import { Close as CloseIcon } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar as AppBarMui, Button, Toolbar } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DataGenerationHistoryButton } from '@/components/DataGenerationHistory';
import { SearchAssistant } from '@/components/SearchAssistant';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import FullLogo from '../../../assets/images/FullLogo.svg';
import WLogo from '../../../assets/images/WLogo.svg';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { ADMINS_PATH, DATA_DOWNLOADS_PATH, ROOT_PATH, SETTINGS_PATH } from '../../utils/constants';
import { GlobalSearch } from '../GlobalSearch';
import { ShinyLink } from '../Shiny';
import { useAppBarStyles } from './AppBar.style';
import { DropDown } from './DropDown';
import { useLinks } from './hooks/useLinks';
import { MobileTray } from './MobileTray';
import { SignOutButton } from './SignOutButton';

const getUserInitials = (user, userLoading) => {
  if (userLoading) return '';
  if (!user || !user.firstName || !user.lastName) return 'W';
  return user.firstName[0] + user.lastName[0];
};

const MenuItem = ({ title, link, type, menuItems, page }) => {
  const classes = useAppBarStyles();
  const path = location.pathname;
  const { isUserEntitled } = useCurrentUser();

  if (type === 'menu') {
    return <DropDown key={title} link={{ title, link, type, menuItems }} />;
  }

  if (type === 'shiny') {
    return (
      <ShinyLink
        key={title}
        link={{ title, link, type, menuItems, page }}
        styles={{ link: classes.link }}
        disabled={!isUserEntitled(DATA_DOWNLOADS_PATH)}
      />
    );
  }

  if (type === 'href') {
    return (
      <Button
        color='inherit'
        underline='none'
        className={classes.link}
        onClick={() => window.open(link, '_blank')}
      >
        {title}
      </Button>
    );
  }

  return (
    <Button
      component={Link}
      to={`${link}`}
      color='inherit'
      underline='none'
      disabled={!isUserEntitled(link)}
      className={path.indexOf(link) !== -1 ? classes.selectedLink : classes.link}
    >
      {title}
    </Button>
  );
};

export const AppBar = ({ forceNewTab }) => {
  const { globalDownloads } = useGetFeatureFlags();
  const classes = useAppBarStyles();
  const { currentUser, loading: userLoading } = useCurrentUser();
  const isLgMediaQueryPlus = useMediaQuery('(min-width:1536px)');
  const isMobile = useMediaQuery('(max-width:750px)');
  const [openMobileTray, setOpenMobileTray] = useState(false);
  const links = useLinks();

  let Logo, logoWidth, logoHeight;
  if (isLgMediaQueryPlus) {
    Logo = FullLogo;
    logoHeight = '45px';
    logoWidth = '172px';
  } else {
    Logo = WLogo;
    logoHeight = '45px';
    logoWidth = '50px';
  }

  const WELLIGENGE_LOGO_ID = 'welligence_logo';

  return (
    <>
      <AppBarMui position='sticky' className={classes.root}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.left}>
            <div>
              <Link to={ROOT_PATH}>
                <img
                  id={WELLIGENGE_LOGO_ID}
                  src={Logo}
                  alt='Welligence-Logo'
                  width={logoWidth}
                  height={logoHeight}
                />
              </Link>
            </div>
            {!isMobile &&
              links?.map((link, i) => <MenuItem key={`${i}-${link.title}`} {...link} />)}
          </div>
          {(!openMobileTray || !isMobile) && <GlobalSearch />}
          {(!openMobileTray || !isMobile) && globalDownloads && <DataGenerationHistoryButton />}
          {isMobile ? (
            <Button
              onClick={() => {
                setOpenMobileTray(!openMobileTray);
              }}
              color='inherit'
              underline='none'
              className={classes.link}
            >
              {openMobileTray ? <CloseIcon /> : <MenuIcon />}
            </Button>
          ) : (
            // biome-ignore lint/correctness/useUniqueElementIds: // its for the tutorial
            <div id='appbar-menu-button' className={classes.right}>
              <DropDown
                link={{
                  title: getUserInitials(currentUser, userLoading),
                  tooltip: 'Settings and Sign out',
                  menuItems: [
                    {
                      title: 'Settings',
                      link: SETTINGS_PATH,
                    },
                    // Only show Admins portal to admins:
                    ...(currentUser?.admin
                      ? [
                          {
                            title: 'Admins portal',
                            link: ADMINS_PATH,
                          },
                        ]
                      : []),
                    { type: 'jsx', jsx: <SignOutButton key='sign-out' /> },
                  ],
                }}
                isUserMenu
              />
            </div>
          )}
        </Toolbar>
      </AppBarMui>
      {openMobileTray && isMobile && (
        <MobileTray
          forceNewTab={forceNewTab}
          onButtonClick={() => setOpenMobileTray(false)}
          onCloseTray={() => {
            setOpenMobileTray(!openMobileTray);
          }}
        />
      )}
      <SearchAssistant />
    </>
  );
};
